// MenuBadgeProvider — счётчики активных задач для пунктов sidebar (AML, KYC).
//
// Зачем нужен Context, а не локальный state в каждом компоненте:
//   - badge показывается в navbar и должен обновляться независимо от того,
//     открыт ли AML/KYC экран
//   - autorefresh 60s — общий, чтобы не делать N параллельных запросов
//   - другие компоненты (Dashboard) могут читать те же счётчики
//
// Endpoints:
//   GET /aml/v1/alerts/count   → pending+in_review AML алерты
//   GET /kyc/v1/count          → PENDING+ON_HOLD applicant'ы
//
// Оба endpoint'а fail-open: при ошибке возвращают 0, бейдж исчезает (UI
// не должен ломаться из-за временной недоступности admin-api).
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ApiService from '../helpers/apiService';
import { hasAccess } from '../helpers/access';

const REFRESH_INTERVAL_MS = 60_000; // 60 секунд — компромисс между свежестью и нагрузкой

export interface MenuCounters {
  aml: number;
  kyc: number;
}

interface MenuBadgeContextValue {
  counters: MenuCounters;
  // AML-13 (v67): timestamp последнего успешного refresh — sidebar badge
  // показывает «Обновлено в HH:MM» в title-tooltip, чтобы оператор понимал
  // свежесть. null до первого успешного запроса.
  lastUpdatedAt: Date | null;
  refresh: () => Promise<void>;
}

const MenuBadgeContext = createContext<MenuBadgeContextValue>({
  counters: { aml: 0, kyc: 0 },
  lastUpdatedAt: null,
  refresh: async () => {},
});

export const MenuBadgeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [counters, setCounters] = useState<MenuCounters>({ aml: 0, kyc: 0 });
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    // Гейт по доступу: НЕ дёргаем count-эндпоинты, на которые у оператора нет прав
    // (/aml/v1/alerts/count → AccessAmlList, /kyc/v1/count → AccessKycList; AML-15).
    // Иначе для оператора без aml/kyc каждый из них отдаёт законный 403, а
    // axios-интерсептор трактует 403 как «токен протух» → refresh-шторм каждые 60с
    // → churn токена и периодический «сброс» левого меню. hasAccess fail-open
    // (пустой набор → true), так что для полного доступа/админа зовём оба.
    const canAml = hasAccess('amlList');
    const canKyc = hasAccess('kycList');
    // allSettled — если KYC gRPC недоступен, AML счётчик всё равно обновится
    // (и наоборот). Promise.all обнулял бы оба счётчика при любой ошибке.
    const [amlRes, kycRes] = await Promise.allSettled([
      canAml ? ((ApiService as any).amlGetActiveCount() as Promise<number>) : Promise.resolve(0),
      canKyc ? ((ApiService as any).kycGetActiveCount() as Promise<number>) : Promise.resolve(0),
    ]);
    setCounters((prev) => ({
      aml: amlRes.status === 'fulfilled' ? amlRes.value : prev.aml,
      kyc: kycRes.status === 'fulfilled' ? kycRes.value : prev.kyc,
    }));
    // Обновляем timestamp если хотя бы один из счётчиков успешно загрузился.
    // Если оба failed — оставляем предыдущий timestamp (показывает «устаревшее»).
    if (amlRes.status === 'fulfilled' || kycRes.status === 'fulfilled') {
      setLastUpdatedAt(new Date());
    }
  }, []);

  useEffect(() => {
    refresh();
    intervalRef.current = setInterval(() => {
      // Не дёргаем API когда таб неактивен — экономия трафика и снижение нагрузки
      // на admin-api для операторов с открытыми, но не активными вкладками.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      refresh();
    }, REFRESH_INTERVAL_MS);
    // Refresh при возврате фокуса вкладки — оператор после lunch-break сразу
    // видит свежие данные, не ждёт следующего тика.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  return (
    <MenuBadgeContext.Provider value={{ counters, lastUpdatedAt, refresh }}>
      {children}
    </MenuBadgeContext.Provider>
  );
};

export const useMenuBadge = (): MenuBadgeContextValue => useContext(MenuBadgeContext);
